import { Autocomplete, Box, Button, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { blueFadeColor, white } from "../../config/ColorObj";


const CustomTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        height: 42,
        borderRadius: "10px",
        transition: "border-color 0.2s ease",

        "& fieldset": {
            borderColor: theme.palette.grey[300],
        },

        "&:hover fieldset": {
            borderColor: blueFadeColor, // ✅ hover blue
        },

        "&.Mui-focused fieldset": {
            borderColor: blueFadeColor, // ✅ focus blue
            borderWidth: 1.5,
        },
    },

    "& .MuiOutlinedInput-input": {
        padding: "8px 10px",
        fontSize: "14px",
    },

    "& .MuiOutlinedInput-input::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 0.8,
    },
}));

/**
 * Common Autocomplete Component
 * Supports:
 * - Single select
 * - Multiple select
 * - Custom renderTags
 * - Custom Listbox
 * - Error handling
 */





/* ============================
   COMMON LISTBOX
============================ */
const SelectActionListbox = React.forwardRef(function SelectActionListbox(
    props,
    ref
) {
    const {
        children,
        onSelectAll,
        onRemoveAll,
        ...other
    } = props;

    return (
        <Box ref={ref} {...other}>
            {/* ACTION BAR */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid #e5e7eb",
                    backgroundColor: white,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Button size="small" onClick={onSelectAll}>
                    Select All
                </Button>

                <Button
                    size="small"
                    color="error"
                    onClick={onRemoveAll}
                >
                    Remove All
                </Button>
            </Box>

            {/* OPTIONS */}
            <Box>{children}</Box>
        </Box>
    );
});

const AutocompleteCompo = ({
    multiple = false,
    options = [],
    value,
    onChange,
    inputValue,
    onInputChange,
    getOptionLabel = (option) => option?.name || "",
    placeholder = "Select",
    error = false,
    helperText = "",
    size = "small",

    /* NEW – select all / remove all */
    enableSelectActions = false,
    onSelectAll,
    onRemoveAll,

    /* optional overrides */
    renderTags,

    sx = {},
    inputSx = {},

    ...rest
}) => {
    return (
        <Autocomplete
            multiple={multiple}
            size={size}
            options={options}
            value={value}
            inputValue={inputValue}
            onInputChange={onInputChange}
            getOptionLabel={getOptionLabel}
            disableCloseOnSelect={multiple}
            onChange={onChange}

            /* 🔑 AUTO-INJECT LISTBOX */
            ListboxComponent={
                multiple && enableSelectActions
                    ? React.forwardRef((props, ref) => (
                        <SelectActionListbox
                            {...props}
                            ref={ref}
                            onSelectAll={onSelectAll}
                            onRemoveAll={onRemoveAll}
                        />
                    ))
                    : undefined
            }

            renderTags={renderTags}
            renderInput={(params) => (
                <CustomTextField
                    {...params}
                    multiline
                    placeholder={placeholder}
                    error={Boolean(error)}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        sx: {
                            alignItems: multiple ? "flex-start" : "center",
                            flexWrap: multiple ? "wrap" : "nowrap",
                            minHeight: "40px",
                            overflow: "hidden",
                            ...inputSx,
                        },
                    }}
                />
            )}

            sx={{
                "& .MuiAutocomplete-inputRoot": {
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    height: "auto",
                    overflow: "hidden",
                },
                "& .MuiInputBase-root": {
                    height: "auto",
                    overflow: "hidden",
                },
                "& .MuiAutocomplete-tag": {
                    maxWidth: "calc(100% - 16px)",
                },
                ...sx,
            }}
            {...rest}
        />
    );
};

export default AutocompleteCompo;
